import React from "react";
import classNames from "classnames";
import { useMutationObserver } from "web/react/hooks/use-mutation-observer/use-mutation-observer";
import styles from "./accordion.css";

type AccordionHook = { accordionName: string | undefined; nonInteraction: boolean };

interface AccordionProps {
    initialExpanded?: boolean;
    children: React.ReactNode;
    name?: string;
    onAccordionOpened?: (param: AccordionHook) => void;
    onAccordionClosed?: (param: AccordionHook) => void;
}

interface AccordionTitleProps {
    children: React.ReactNode;
    className?: string;
    activeClassName?: string;
}

interface AccordionContentProps {
    children: React.ReactNode;
    className?: string;
}

interface AccordionContextProps {
    expanded: boolean;
    onToggle: () => void;
}

const AccordionContext = React.createContext<AccordionContextProps>({
    expanded: false,
    onToggle: () => {},
});

function AccordionContent({ children, className }: AccordionContentProps): React.ReactElement {
    const { expanded } = React.useContext(AccordionContext);
    const [maxHeight, setMaxHeight] = React.useState<number>(0);
    const contentRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (contentRef.current) {
            setMaxHeight(contentRef.current.scrollHeight);
        }
    }, [contentRef]);

    // Mutating the .current reffered element has no effect on rendering
    // therefore does not work with useEffect, useCallback or Callback Refs
    // useMutationObserver uses MutationObserver to monitor element changes
    // This is necessary for DOM changes made by 3rd party code for example
    function onChange(): void {
        if (contentRef.current) {
            setMaxHeight(contentRef.current.scrollHeight);
        }
    }
    // We specifically want to observe text changes of children
    const observerOptions = {
        childList: true,
        subtree: true,
        characterData: true,
    };
    useMutationObserver({ callback: onChange, ref: contentRef, observerOptions });

    return (
        <div
            ref={contentRef}
            className={classNames(
                styles.content,
                { [styles.contentExpanded]: expanded },
                className
            )}
            style={{
                maxHeight: expanded ? maxHeight : 0,
            }}
        >
            {children}
        </div>
    );
}

function AccordionTitle({
    children,
    className,
    activeClassName = "",
}: AccordionTitleProps): React.ReactElement {
    const { onToggle, expanded } = React.useContext(AccordionContext);

    return (
        <div
            className={classNames(styles.title, { [styles.titleActive]: expanded }, className, {
                [activeClassName]: expanded,
            })}
            onClick={onToggle}
        >
            {children}
        </div>
    );
}

function Accordion({
    initialExpanded,
    children,
    name,
    onAccordionOpened,
    onAccordionClosed,
}: AccordionProps): React.ReactElement {
    const [expanded, setExpanded] = React.useState<boolean>(initialExpanded || false);

    React.useEffect(() => {
        const nonInteraction = true;
        if (initialExpanded && onAccordionOpened) {
            onAccordionOpened({ accordionName: name, nonInteraction });
        } else if (!initialExpanded && onAccordionClosed) {
            onAccordionClosed({ accordionName: name, nonInteraction });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onToggle(): void {
        const nonInteraction = false;
        if (expanded && onAccordionClosed) {
            onAccordionClosed({ accordionName: name, nonInteraction });
        } else if (!expanded && onAccordionOpened) {
            onAccordionOpened({ accordionName: name, nonInteraction });
        }

        setExpanded(!expanded);
    }

    return (
        <AccordionContext.Provider value={{ expanded, onToggle }}>
            <div className={styles.accordion}>{children}</div>
        </AccordionContext.Provider>
    );
}

Accordion.Content = AccordionContent;
Accordion.Title = AccordionTitle;

export default Accordion;
